import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import {
  Globe2, ArrowRight,
  ShieldCheck, Zap, Sparkles,
  CalendarCheck, LogIn, Search, Bed, BarChart3, Cloud, Users2,
  Wifi, Lock, Tv2, PhoneCall, Flame, SlidersHorizontal,
  Gauge, HeadphonesIcon, Layers, Server,
} from "lucide-react";
import SEO from "@/components/SEO";
import HeroImmersive from "@/components/HeroImmersive";
import IntegrationWall from "@/components/IntegrationWall";
import PMSExperience from "@/components/PMSExperience";
import OperationsStory from "@/components/OperationsStory";
import AnalyticsShowcase from "@/components/AnalyticsShowcase";
import BookingFlow from "@/components/BookingFlow";
import ChannelMarquee from "@/components/ChannelMarquee";
import ChaosVsControl from "@/components/ChaosVsControl";
import BookingEngine from "@/components/BookingEngine";
import OperationalWorkflow from "@/components/OperationalWorkflow";
import DarkCTA from "@/components/DarkCTA";
import FinalCTA from "@/components/FinalCTA";
import { useT } from "@/hooks/useT";
import { useI18n } from "@/contexts/I18nContext";

export default function Home() {
  const t = useT();
  const { lang } = useI18n();

  const FEATURES = [
    [CalendarCheck, t("Menaxhim Rezervimesh", "Reservation Management"), t("Sistem qendror për të gjitha rezervimet — direkte dhe nga OTA.", "One central system for all reservations — direct and from OTAs.")],
    [LogIn,         t("Check-In / Check-Out",  "Check-In / Check-Out"),   t("Procese të shpejta në recepsion, me ndërfaqe të thjeshtë.", "Fast front-desk workflows with a clean, simple interface.")],
    [Search,        t("Booking Engine",         "Booking Engine"),         t("Merrni rezervime direkte nga website-i pa komisione.", "Take commission-free direct bookings from your website.")],
    [Globe2,        t("Channel Manager",        "Channel Manager"),        t("Sinkronizim automatik me Booking.com, Airbnb, Expedia, Agoda.", "Automatic sync with Booking.com, Airbnb, Expedia and Agoda.")],
    [Sparkles,      t("Housekeeping",           "Housekeeping"),           t("Menaxhim i pastrimit dhe statusit të dhomave në kohë reale.", "Manage cleaning and room status in real time.")],
    [BarChart3,     t("Raporte Financiare",     "Financial Reports"),      t("Të ardhurat, ADR, RevPAR dhe analiza të detajuara.", "Revenue, ADR, RevPAR and deep operational analytics.")],
    [Bed,           t("Menaxhim Dhomash",       "Room Management"),        t("Statusi i çdo dhome, kategoritë, çmimet dhe disponueshmëria.", "Track status, categories, rates and availability for every room.")],
    [Zap,           t("Smart Hotel",            "Smart Hotel"),            t("Integrim me çelësa smart, IPTV, WiFi dhe kontroll dhomash.", "Integrated with smart locks, IPTV, WiFi and in-room controls.")],
    [Cloud,         t("Cloud Access",           "Cloud Access"),           t("Akses i sigurt nga çdo pajisje, kudo që ndodheni.", "Secure access from any device, anywhere.")],
    [Users2,        t("Multi User System",      "Multi-user System"),      t("Role dhe leje për çdo anëtar të ekipit tuaj.", "Roles and permissions for every member of your team.")],
  ];

  const SMART_HOTEL = [
    [Lock,              t("Smart Locks",         "Smart Locks"),         t("Akses pa çelës dhe online check-in për klientët.", "Keyless entry and online check-in for guests.")],
    [Tv2,               t("IPTV",                "IPTV"),                t("TV i lidhur me sistemin e hotelit dhe shërbimet.", "TV tied into hotel services and the guest experience.")],
    [Wifi,              t("WiFi Systems",        "WiFi Systems"),        t("Menaxhim i rrjetit dhe akses i kontrolluar për klientët.", "Managed network and controlled guest access.")],
    [PhoneCall,         t("IP Phones",           "IP Phones"),           t("Telefoni VoIP të integruar me PMS-in.", "VoIP phones integrated directly with the PMS.")],
    [SlidersHorizontal, t("Smart Room Controls", "Smart Room Controls"), t("Drita, ngrohje dhe perde nga një ndërfaqe.", "Lights, climate and blinds from a single interface.")],
    [Flame,             t("Fire Alarm Systems",  "Fire Alarm Systems"),  t("Lidhje me sistemet e sigurisë dhe alarmet.", "Connected with safety systems and alarms.")],
  ];

  const WHY = [
    [Layers,         t("Platformë Moderne",        "Modern Platform"),        t("E ndërtuar me teknologjitë më të reja për një përvojë profesionale.", "Built on the latest tech for a truly professional experience.")],
    [Gauge,          t("Performancë e Shpejtë",    "Fast Performance"),       t("Ngarkim i shpejtë dhe veprime në kohë reale, pa pritje.", "Snappy interfaces and real-time actions — no waiting.")],
    [HeadphonesIcon, t("Mbështetje Profesionale",  "Professional Support"),   t("Ekipi ynë përgjigjet brenda 24 orëve gjatë ditëve të punës.", "Our team replies within 24 hours on business days.")],
    [Server,         t("Infrastrukturë Skalabile", "Scalable Infrastructure"),t("E ndërtuar për të rritur së bashku me hotelin tuaj.", "Designed to grow alongside your property.")],
    [Cloud,          t("Cloud Based",              "Cloud-based"),            t("Pa instalime, pa servera lokal — gjithçka në cloud.", "No installs, no local servers — everything runs in the cloud.")],
    [ShieldCheck,    t("Siguri Enterprise",        "Enterprise Security"),    t("Të dhënat janë të enkriptuara dhe të rezervuara çdo ditë.", "Encrypted data with daily backups.")],
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Akomoda",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: t(
      "Program profesional për menaxhimin e hoteleve — PMS, booking engine, channel manager, housekeeping dhe raporte për hotele, resorte dhe apartamente.",
      "Professional hotel management software — PMS, booking engine, channel manager, housekeeping and reports for hotels, resorts and apartments."
    ),
    offers: { "@type": "Offer", priceCurrency: "EUR" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "47" },
  };

  return (
    <>
      <SEO
        title={t(
          "Program për Hotele — PMS, Booking Engine, Channel Manager",
          "Hotel Software — PMS, Booking Engine, Channel Manager"
        )}
        description={t(
          "Akomoda — program profesional për menaxhimin e hotelit. Sistem PMS modern, booking engine, channel manager, housekeeping, raporte financiare dhe smart hotel në një platformë të vetme.",
          "Akomoda — professional hotel management software. Modern PMS, booking engine, channel manager, housekeeping, financial reports and smart-hotel in one platform."
        )}
        ogImage="/akomoda-hero.png"
        schema={schema}
      />

      {/* 1. IMMERSIVE HERO */}
      <HeroImmersive />

      {/* 1b. CHANNEL MARQUEE — animated OTA logos */}
      <ChannelMarquee />

      {/* 1b·3. NGA KAOSI NË KONTROLL — comparison + feature grid */}
      <ChaosVsControl />

      {/* 1b·4. BOOKING ENGINE — direct reservations showcase */}
      <BookingEngine />

      {/* 1b·5. OPERATIONAL WORKFLOW — PMS dashboard showcase */}
      <OperationalWorkflow />

      {/* 2. OTA ECOSYSTEM — DARK */}
      <section className="relative overflow-hidden surface-dark-2 text-white" data-testid="ota-section">
        <div className="orb orb-orange -top-32 right-1/4 h-[420px] w-[420px] opacity-70" aria-hidden />
        <div className="orb orb-violet bottom-0 left-0 h-[360px] w-[360px] opacity-50" aria-hidden />
        <div className="absolute inset-0 bg-dot-dark opacity-40" aria-hidden />

        <div className="container-akm relative section">
          <div className="grid items-end gap-6 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="eyebrow-dark">Channel Manager</div>
              <h2 className="mt-4 font-display text-[clamp(2rem,1.4rem+2.4vw,3rem)] font-extrabold tracking-[-0.03em] leading-[1.06] text-white">
                {t("Të gjitha rezervimet,", "Every booking,")}<br />
                <span className="text-[#FF7A00]">{t("në një kalendar të vetëm.", "in one single calendar.")}</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="lead-dark text-[15.5px]">
                {t(
                  "Çdo rezervim që vjen nga Booking.com, Airbnb, Expedia, Agoda, Google Hotels ose Vrbo hyn vetë në kalendarin tuaj. Çdo dhomë e shitur largohet menjëherë nga të gjitha kanalet — pa rrezikuar rezervime të dyfishta.",
                  "Every reservation from Booking.com, Airbnb, Expedia, Agoda, Google Hotels or Vrbo flows straight into your calendar. Every sold room is instantly removed from every channel — no risk of double bookings."
                )}
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              [t("I shpejtë", "Instant"), t("Sinkronizimi në çast", "Real-time sync")],
              [t("8 kanale",  "8 channels"), t("Të lidhura drejtpërdrejt", "Directly connected")],
              ["0",           t("Rezervime të dyfishta", "Double bookings")],
              ["24/7",        t("Pa ndërprerje", "No downtime")],
            ].map(([v, k]) => (
              <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="font-display text-[24px] font-extrabold tracking-tight text-white">{v}</div>
                <div className="text-[12px] text-white/55">{k}</div>
              </div>
            ))}
          </div>

          <div className="hairline-dark my-16" />

          <IntegrationWall />

          <div className="mt-14 flex flex-wrap gap-3">
            <Link to="/channel-manager" className="btn-primary" data-testid="ota-cta">
              {t("Më shumë për Channel Manager", "More about Channel Manager")} <ArrowRight size={14} />
            </Link>
            <Link to="/integrimet" className="btn-on-dark">{t("Të gjitha integrimet", "All integrations")}</Link>
          </div>
        </div>
      </section>

      {/* 3. PMS EXPERIENCE — LIGHT, LARGE FLOATING CALENDAR */}
      <PMSExperience />

      {/* 4. BOOKING ENGINE FLOW */}
      <section className="relative overflow-hidden bg-surface-2">
        <div className="absolute inset-0 bg-dot opacity-40 mask-fade-y" aria-hidden />
        <div className="container-akm relative section">
          <div className="grid items-end gap-6 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="eyebrow">Booking Engine</div>
              <h2 className="h2-akm mt-3">
                {t("Rezervime direkte, pa komisione.", "Direct bookings, zero commissions.")}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="lead">
                {t(
                  "Booking engine i Akomoda lidhet direkt me website-in tuaj. Klienti rezervon online — dhe rezervimi kalon automatikisht në kalendarin e recepsionit.",
                  "Akomoda's booking engine plugs directly into your website. Guests book online — and the reservation lands straight in the front-desk calendar."
                )}
              </p>
            </div>
          </div>

          <div className="mt-14">
            <BookingFlow />
          </div>

          <div className="mt-14 grid gap-4 md:grid-cols-4">
            {[
              [Zap,         t("Pa vonesa",             "No delays"),           t("Rezervimi shfaqet menjëherë në PMS",       "The booking hits the PMS instantly")],
              [ShieldCheck, t("Pagesa të sigurta",     "Secure payments"),     t("Stripe ose pagesë në recepsion",           "Stripe or pay-at-property")],
              [Globe2,      t("Shqip, anglisht, italisht", "Multi-language"),  t("Multi-valutë për çdo klient",              "Multi-currency for every guest")],
              [Sparkles,    t("Me pamjen e hotelit tuaj", "Your hotel's look"),t("Logo, ngjyra dhe stil i personalizuar",   "Custom logo, colours and styling")],
            ].map(([Icon, tt, d]) => (
              <div key={tt} className="card-akm">
                <Icon size={20} className="text-[#FF7A00]" />
                <div className="mt-3 font-display text-[15px] font-bold text-ink">{tt}</div>
                <div className="mt-1 text-[13px] text-slate2">{d}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/booking-engine" className="btn-primary">
              {t("Më shumë për Booking Engine", "More about Booking Engine")} <ArrowRight size={14} />
            </Link>
            <Link to="/demo" className="btn-ghost">{t("Kërko Demo", "Request Demo")}</Link>
          </div>
        </div>
      </section>

      {/* 5. OPERATIONS — STORYTELLING TIMELINE */}
      <OperationsStory />

      {/* 6. REVENUE & ANALYTICS — DARK PREMIUM */}
      <AnalyticsShowcase />

      {/* 7b. SMART HOTEL — premium light section */}
      <section className="relative overflow-hidden bg-white" aria-labelledby="smart-hotel-heading">
        <div
          className="pointer-events-none absolute -top-32 right-1/4 h-[480px] w-[480px] rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.10),transparent_70%)] blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute bottom-0 left-0 h-[360px] w-[360px] rounded-full bg-[radial-gradient(closest-side,rgba(56,109,212,0.06),transparent_70%)] blur-3xl"
          aria-hidden
        />

        <div className="container-akm relative section">
          <div className="grid items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#FFD9B5] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#EA580C] shadow-[0_4px_14px_-6px_rgba(242,99,34,0.18)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A00]" />
                Smart Hotel
              </div>
              <h2
                id="smart-hotel-heading"
                className="mt-6 font-display font-extrabold tracking-[-0.04em] text-[#0F172A]"
                style={{ fontSize: "clamp(1.95rem, 1.2rem + 2.8vw, 3.25rem)", lineHeight: 1.05 }}
              >
                {t("Integrime për një", "Built for the")}<br />
                <span className="bg-gradient-to-r from-[#0F172A] via-[#0F172A] to-[#FF7A00] bg-clip-text text-transparent">
                  {t("hotel të lidhur plotësisht.", "fully connected hotel.")}
                </span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[16px] leading-[1.8] text-[#475569]">
                {t(
                  "Akomoda lidhet me sistemet kryesore të Smart Hotel — çelësa pa kontakt, IPTV, WiFi, telefoni IP dhe kontroll dhomash — për të krijuar një përvojë të integruar për klientin dhe ekipin tuaj.",
                  "Akomoda plugs into the leading smart-hotel systems — keyless entry, IPTV, WiFi, IP phones and in-room controls — for one seamless guest and staff experience."
                )}
              </p>
            </div>
          </div>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SMART_HOTEL.map(([Icon, title, desc]) => (
              <article
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-[#FFD9B5] hover:shadow-[0_18px_40px_-18px_rgba(242,99,34,0.28)]"
              >
                <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.12),transparent)] opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-[#FFF1E5] to-[#FFE0CC] text-[#EA580C] ring-1 ring-[#FFD9B5]/60 transition-transform group-hover:scale-105">
                  <Icon size={18} />
                </span>
                <h3 className="mt-5 font-display text-[16px] font-bold tracking-[-0.02em] text-[#0F172A]">{title}</h3>
                <p className="mt-2 text-[13.5px] leading-[1.65] text-[#64748B]">{desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 7c. WHY CHOOSE AKOMODA */}
      <section className="bg-white" aria-labelledby="why-heading">
        <div className="container-akm section">
          <div className="mx-auto max-w-2xl text-center">
            <div className="eyebrow inline-flex">{t("Pse Akomoda", "Why Akomoda")}</div>
            <h2 id="why-heading" className="h2-akm mt-4">
              {t("Pse hotelet zgjedhin Akomoda", "Why hotels choose Akomoda")}
            </h2>
            <p className="lead mt-5">
              {t(
                "Një platformë moderne, e shpejtë dhe e besueshme — e ndërtuar për hotele që duan kontroll real mbi operacionet e tyre.",
                "A modern, fast and reliable platform — built for hotels that want real control over their operations."
              )}
            </p>
          </div>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map(([Icon, title, desc]) => (
              <article
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-line bg-white p-7 transition-all hover:-translate-y-0.5 hover:border-[#FF7A00]/40 hover:shadow-cinematic-light"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl border border-line bg-[#FFF7ED] text-[#FF7A00] transition-colors group-hover:bg-[#FF7A00] group-hover:text-white">
                  <Icon size={18} />
                </span>
                <h3 className="mt-5 font-display text-[16.5px] font-bold tracking-[-0.02em] text-ink">{title}</h3>
                <p className="mt-2 text-[14px] leading-[1.7] text-slate2">{desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 7e. DARK CTA — "Më pak procese manuale. Më shumë kontroll." */}
      <DarkCTA />

      {/* 8. FINAL CTA */}
      <FinalCTA />
    </>
  );
}
